import { useState } from 'react';

import { Loader2, Wand2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useDraftScenario } from '@/hooks/useScenarios';

import type { ScenarioDraft } from '@/lib/api/scenarios.api';

export const NEW_SCENARIO_PATH = '/agent-improvement/scenarios/new';

/** Router state the new-scenario form reads to pre-fill itself. */
export interface NewScenarioRouterState {
  draft?: ScenarioDraft;
  description?: string;
}

interface ScenarioDraftDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const ScenarioDraftDialog = ({ open, onOpenChange }: ScenarioDraftDialogProps) => {
  const navigate = useNavigate();
  const [description, setDescription] = useState('');
  const draftMutation = useDraftScenario();

  const close = () => {
    onOpenChange(false);
    setDescription('');
  };

  const handleDraft = async () => {
    const text = description.trim();
    if (!text) return;
    try {
      const draft = await draftMutation.mutateAsync(text);
      const state: NewScenarioRouterState = {
        draft: { ...draft, description: draft.description ?? text },
        description: text,
      };
      close();
      navigate(NEW_SCENARIO_PATH, { state });
    } catch {
      // The hook already toasts the error; keep the dialog open so the text is not lost.
    }
  };

  const handleBlank = () => {
    const state: NewScenarioRouterState = { description: description.trim() };
    close();
    navigate(NEW_SCENARIO_PATH, { state });
  };

  return (
    <Dialog open={open} onOpenChange={(next) => (next ? onOpenChange(true) : close())}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>New scenario</DialogTitle>
          <DialogDescription>
            Describe the conversation to test in one paragraph: who the customer is, what they want,
            and what the agent must do. AI drafts the brief and checks; you review before saving.
          </DialogDescription>
        </DialogHeader>
        <textarea
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          rows={6}
          disabled={draftMutation.isPending}
          placeholder="A returning customer calls about a used Golf they saw online, asks whether it is still available and wants to book a test drive this Saturday. The agent should check stock, offer a slot and never quote a monthly finance price."
          className="border-input bg-background focus-visible:ring-ring w-full rounded-md border px-3 py-2 text-sm focus-visible:ring-2 focus-visible:outline-none"
        />
        <DialogFooter className="gap-2 sm:justify-between">
          <Button
            type="button"
            variant="outline"
            onClick={handleBlank}
            disabled={draftMutation.isPending}
          >
            Start blank
          </Button>
          <Button
            type="button"
            onClick={handleDraft}
            disabled={draftMutation.isPending || description.trim().length === 0}
          >
            {draftMutation.isPending ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <Wand2 className="mr-2 h-4 w-4" />
            )}
            Draft with AI
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
